/**
 * 分享對話框（#24）：對某個檔案（個人硬碟 / 租戶硬碟瀏覽列）建立 access-grant 分享。
 * 對象走同租戶 active 使用者自動完成；權限 baseline 唯讀。
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Typography, Box, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../../../contexts/MessageContext';
import { createShare, getTenantUsers, SourceKind, TenantUser } from './services/driveShareService';

interface Props {
  open: boolean;
  onClose: () => void;
  sourceKind: SourceKind;
  sourceId: number;
  filename: string;
  resourceKind?: 'file' | 'folder';   // Phase 2.5：分享檔或資料夾
  onShared?: () => void;
}

const ShareDialog: React.FC<Props> = ({ open, onClose, sourceKind, sourceId, filename,
                                        resourceKind = 'file', onShared }) => {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [targetKind, setTargetKind] = useState<'user' | 'org'>('user');
  const [toUserId, setToUserId] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTargetKind('user'); setToUserId(''); setNote('');
    getTenantUsers().then(setUsers).catch(showApiError);
  }, [open, showApiError]);

  const canSubmit = targetKind === 'org' || toUserId !== '';

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await createShare({
        source_kind: sourceKind, source_id: sourceId, resource_kind: resourceKind,
        shared_to_kind: targetKind,
        to_user_id: targetKind === 'user' ? Number(toUserId) : undefined,
        note: note || null,
      });
      showSuccess('SYS020001', { name: filename });
      onShared?.();
      onClose();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('driveShare.share', '分享')} — {filename}
        {resourceKind === 'folder' && ` (${t('driveShare.resourceKindFolder', '資料夾')})`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl>
            <FormLabel>{t('driveShare.selectUser', '選擇對象')}</FormLabel>
            <RadioGroup row value={targetKind} onChange={(e) => setTargetKind(e.target.value as 'user' | 'org')}>
              <FormControlLabel value="user" control={<Radio />} label={t('driveShare.shareToUser', '分享給使用者')} />
              <FormControlLabel value="org" control={<Radio />} label={t('driveShare.shareToOrg', '分享給組織')} />
            </RadioGroup>
          </FormControl>
          {targetKind === 'user' ? (
            <TextField
              select fullWidth label={t('driveShare.selectUser', '選擇對象')}
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.username || u.account || `#${u.id}`}</MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('driveShare.shareToOrgHint', '分享給您所屬的組織（全體成員可讀；由管理員收進租戶硬碟）')}
            </Typography>
          )}
          <TextField
            fullWidth multiline minRows={2} label={t('driveShare.note', '分享說明')}
            value={note} onChange={(e) => setNote(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary">
            {t('driveShare.permission', '權限')}: {t('driveShare.permRead', '唯讀')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', '取消')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !canSubmit}>
          {t('driveShare.share', '分享')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
